import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import { useTenantBranding } from '../hooks/useTenantBranding';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSelector } from '../components/navigation/LanguageSelector';

/**
 * AuthLayout — Trainova Responsive Light Brand Split Stage with Tenant Branding Support.
 */
export function AuthLayout() {
  const { t } = useLanguage();
  const params = useParams<{ tenantSlug?: string }>();
  const [searchParams] = useSearchParams();
  const explicitSlug = params.tenantSlug || searchParams.get('tenant') || searchParams.get('org') || searchParams.get('slug') || '';
  const { branding } = useTenantBranding(explicitSlug);

  const loginLogo = branding?.loginLogoUrl;
  const orgName = branding?.name;

  return (
    <div className="auth-stage-wrapper">
      <div className="stage auth-stage">
        <div className="screen auth-screen">
          {/* Brand Left Panel (Hidden on mobile via CSS) */}
          <aside className="brand auth-brand" aria-hidden="true">
            {loginLogo ? (
              <div style={{ marginBottom: '16px' }}>
                <img
                  src={loginLogo}
                  alt={orgName || t('auth.org_logo_alt', undefined, 'Organization Logo')}
                  style={{ maxHeight: '48px', maxWidth: '200px', objectFit: 'contain', display: 'block', background: 'rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '8px' }}
                  onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                />
              </div>
            ) : (
              <div style={{ fontFamily: 'var(--display)', fontSize: '26px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.02em', marginBottom: '16px' }}>
                Trainova<span style={{ color: '#7FB0FF' }}>.ai</span>
              </div>
            )}

            <div>
              <h2>{t('auth.brand_tagline_main', undefined, 'Practice the conversation')} <em>{t('auth.brand_tagline_em', undefined, "before it's real.")}</em></h2>
              <p className="lede">{t('auth.brand_lede', undefined, "Rehearse customer calls with AI personas, get scored on every competency, and prove you're ready.")}</p>
            </div>

            <div className="sim">
              <div className="sim-head">
                <div className="avatar">PS</div>
                <div className="who">{t('auth.brand_sim_who_name', undefined, 'Priya Sharma · Customer')}<span>{t('auth.brand_sim_who_desc', undefined, 'FinTech · Disputed Card Charge · Service')}</span></div>
                <div className="live"><i></i>{t('auth.brand_sim_live', undefined, 'Practice')}</div>
              </div>
              <div className="bubble cust">
                <small>{t('auth.brand_sim_cust_role', undefined, 'Customer')}</small>
                {t('auth.brand_sim_cust_msg', undefined, "I've been charged twice for the same order and nobody's helped me for three days!")}
              </div>
              <div className="bubble me">
                <small>{t('auth.brand_sim_me_role', undefined, 'You (Learner)')}</small>
                {t('auth.brand_sim_me_msg', undefined, 'I understand your frustration, Priya. Let me pull up both transactions and investigate right now.')}
              </div>
              <div className="meters">
                <div className="meter">{t('auth.brand_sim_meter_empathy', undefined, 'Empathy')}<b>4.5<small style={{ fontSize: '11px', color: '#7F90B0' }}> /5</small></b><div className="bar"><i style={{ width: '90%' }}></i></div></div>
                <div className="meter">{t('auth.brand_sim_meter_resolution', undefined, 'Resolution')}<b>4.2<small style={{ fontSize: '11px', color: '#7F90B0' }}> /5</small></b><div className="bar"><i style={{ width: '84%' }}></i></div></div>
                <div className="meter">{t('auth.brand_sim_meter_readiness', undefined, 'Readiness')}<b>84%</b><div className="bar"><i style={{ width: '84%' }}></i></div></div>
              </div>
            </div>

            <div className="flow">
              <span className="on">{t('auth.brand_flow_practice', undefined, 'Practice')}</span><em>→</em><span>{t('auth.brand_flow_assess', undefined, 'Assess')}</span><em>→</em><span>{t('auth.brand_flow_coach', undefined, 'Coach')}</span><em>→</em><span>{t('auth.brand_flow_prove', undefined, 'Prove Readiness')}</span>
            </div>
          </aside>

          {/* Form Right Panel */}
          <main className="form-side auth-form-side">
            <div className="topline auth-topline">
              <div className="mlogo auth-mlogo">
                {loginLogo ? (
                  <img
                    src={loginLogo}
                    alt={orgName || t('auth.org_logo_alt', undefined, 'Organization Logo')}
                    style={{ maxHeight: '36px', maxWidth: '160px', objectFit: 'contain', display: 'block' }}
                    onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                  />
                ) : (
                  <span style={{ fontFamily: 'var(--display)', fontSize: '20px', fontWeight: 700, color: 'var(--navy)' }}>
                    Trainova<span style={{ color: 'var(--blue)' }}>.ai</span>
                  </span>
                )}
                {orgName && !loginLogo && (
                  <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--muted)', background: 'var(--surface)', border: '1px solid var(--line)', padding: '2px 8px', borderRadius: '6px' }}>
                    {orgName}
                  </span>
                )}
              </div>
              <LanguageSelector variant="compact" className="auth-lang" />
            </div>

            <div className="content auth-content">
              <Outlet />
            </div>

            <footer className="foot auth-foot">
              <span>{t('auth.footer_copyright', undefined, '© 2026 Trainova AI Inc.')}</span>
              <nav>
                <a href="#privacy">{t('auth.footer_privacy', undefined, 'Privacy Policy')}</a>
                <a href="#terms">{t('auth.footer_terms', undefined, 'Terms of Service')}</a>
                <a href="#support">{t('auth.footer_support', undefined, 'Support')}</a>
              </nav>
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
}
